// Static IFC inheritance map for the most common element classes. Used
// by the inspector to show the IFC type chain (e.g. IfcWall →
// IfcBuildingElement → IfcElement → IfcProduct → IfcObject →
// IfcObjectDefinition → IfcRoot). For classes not covered the helper
// returns the class itself plus the conservative root chain
// (IfcRoot → IfcObjectDefinition → IfcObject → IfcProduct → IfcElement)
// when the class name suggests a building element.

const ROOT_CHAIN_PRODUCT = [
  'IfcProduct',
  'IfcObject',
  'IfcObjectDefinition',
  'IfcRoot',
]

const ROOT_CHAIN_ELEMENT = ['IfcElement', ...ROOT_CHAIN_PRODUCT]

const ROOT_CHAIN_BUILDING_ELEMENT = [
  'IfcBuildingElement',
  ...ROOT_CHAIN_ELEMENT,
]

const ROOT_CHAIN_DISTRIBUTION_ELEMENT = [
  'IfcDistributionElement',
  ...ROOT_CHAIN_ELEMENT,
]

const ROOT_CHAIN_DISTRIBUTION_FLOW = [
  'IfcDistributionFlowElement',
  ...ROOT_CHAIN_DISTRIBUTION_ELEMENT,
]

const ROOT_CHAIN_FLOW_SEGMENT = [
  'IfcFlowSegment',
  ...ROOT_CHAIN_DISTRIBUTION_FLOW,
]

const ROOT_CHAIN_FLOW_FITTING = [
  'IfcFlowFitting',
  ...ROOT_CHAIN_DISTRIBUTION_FLOW,
]

const ROOT_CHAIN_FLOW_TERMINAL = [
  'IfcFlowTerminal',
  ...ROOT_CHAIN_DISTRIBUTION_FLOW,
]

const ROOT_CHAIN_SPATIAL = [
  'IfcSpatialStructureElement',
  'IfcSpatialElement',
  ...ROOT_CHAIN_PRODUCT,
]

const ROOT_CHAIN_FURNISHING = [
  'IfcFurnishingElement',
  ...ROOT_CHAIN_ELEMENT,
]

const MAP: Record<string, string[]> = {
  // Building elements
  IfcWall: ['IfcWall', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcWallStandardCase: [
    'IfcWallStandardCase',
    'IfcWall',
    ...ROOT_CHAIN_BUILDING_ELEMENT,
  ],
  IfcCurtainWall: ['IfcCurtainWall', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcSlab: ['IfcSlab', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcSlabStandardCase: [
    'IfcSlabStandardCase',
    'IfcSlab',
    ...ROOT_CHAIN_BUILDING_ELEMENT,
  ],
  IfcRoof: ['IfcRoof', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcBeam: ['IfcBeam', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcBeamStandardCase: [
    'IfcBeamStandardCase',
    'IfcBeam',
    ...ROOT_CHAIN_BUILDING_ELEMENT,
  ],
  IfcColumn: ['IfcColumn', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcColumnStandardCase: [
    'IfcColumnStandardCase',
    'IfcColumn',
    ...ROOT_CHAIN_BUILDING_ELEMENT,
  ],
  IfcDoor: ['IfcDoor', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcDoorStandardCase: [
    'IfcDoorStandardCase',
    'IfcDoor',
    ...ROOT_CHAIN_BUILDING_ELEMENT,
  ],
  IfcWindow: ['IfcWindow', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcWindowStandardCase: [
    'IfcWindowStandardCase',
    'IfcWindow',
    ...ROOT_CHAIN_BUILDING_ELEMENT,
  ],
  IfcStair: ['IfcStair', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcStairFlight: ['IfcStairFlight', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcRamp: ['IfcRamp', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcRailing: ['IfcRailing', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcCovering: ['IfcCovering', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcMember: ['IfcMember', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcPlate: ['IfcPlate', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcFooting: ['IfcFooting', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcPile: ['IfcPile', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcChimney: ['IfcChimney', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcShadingDevice: ['IfcShadingDevice', ...ROOT_CHAIN_BUILDING_ELEMENT],
  IfcBuildingElementProxy: [
    'IfcBuildingElementProxy',
    ...ROOT_CHAIN_BUILDING_ELEMENT,
  ],

  // Openings/voids
  IfcOpeningElement: [
    'IfcOpeningElement',
    'IfcFeatureElementSubtraction',
    'IfcFeatureElement',
    ...ROOT_CHAIN_ELEMENT,
  ],

  // Furnishing
  IfcFurnishingElement: [
    'IfcFurnishingElement',
    ...ROOT_CHAIN_ELEMENT,
  ],
  IfcFurniture: ['IfcFurniture', ...ROOT_CHAIN_FURNISHING],
  IfcSystemFurnitureElement: [
    'IfcSystemFurnitureElement',
    ...ROOT_CHAIN_FURNISHING,
  ],

  // MEP
  IfcDistributionElement: ['IfcDistributionElement', ...ROOT_CHAIN_ELEMENT],
  IfcDistributionFlowElement: [
    'IfcDistributionFlowElement',
    ...ROOT_CHAIN_DISTRIBUTION_ELEMENT,
  ],
  IfcFlowSegment: [...ROOT_CHAIN_FLOW_SEGMENT],
  IfcDuctSegment: ['IfcDuctSegment', ...ROOT_CHAIN_FLOW_SEGMENT],
  IfcPipeSegment: ['IfcPipeSegment', ...ROOT_CHAIN_FLOW_SEGMENT],
  IfcCableSegment: ['IfcCableSegment', ...ROOT_CHAIN_FLOW_SEGMENT],
  IfcFlowFitting: [...ROOT_CHAIN_FLOW_FITTING],
  IfcFlowTerminal: [...ROOT_CHAIN_FLOW_TERMINAL],
  IfcAirTerminal: ['IfcAirTerminal', ...ROOT_CHAIN_FLOW_TERMINAL],
  IfcSanitaryTerminal: [
    'IfcSanitaryTerminal',
    ...ROOT_CHAIN_FLOW_TERMINAL,
  ],

  // Spatial
  IfcProject: [
    'IfcProject',
    'IfcContext',
    'IfcObjectDefinition',
    'IfcRoot',
  ],
  IfcSite: ['IfcSite', ...ROOT_CHAIN_SPATIAL],
  IfcBuilding: ['IfcBuilding', ...ROOT_CHAIN_SPATIAL],
  IfcBuildingStorey: ['IfcBuildingStorey', ...ROOT_CHAIN_SPATIAL],
  IfcSpace: ['IfcSpace', ...ROOT_CHAIN_SPATIAL],

  // Type objects
  IfcWallType: [
    'IfcWallType',
    'IfcBuildingElementType',
    'IfcElementType',
    'IfcTypeProduct',
    'IfcTypeObject',
    'IfcObjectDefinition',
    'IfcRoot',
  ],

  // Property/Quantity holders
  IfcPropertySet: [
    'IfcPropertySet',
    'IfcPropertySetDefinition',
    'IfcPropertyDefinition',
    'IfcRoot',
  ],
  IfcElementQuantity: [
    'IfcElementQuantity',
    'IfcQuantitySet',
    'IfcPropertySetDefinition',
    'IfcPropertyDefinition',
    'IfcRoot',
  ],

  // Materials
  IfcMaterial: ['IfcMaterial', 'IfcMaterialDefinition'],
  IfcMaterialLayer: ['IfcMaterialLayer', 'IfcMaterialDefinition'],
  IfcMaterialLayerSet: ['IfcMaterialLayerSet', 'IfcMaterialDefinition'],
}

// Case-insensitive index built once.
const MAP_BY_LOWER: Record<string, string> = Object.keys(MAP).reduce(
  (acc, k) => {
    acc[k.toLowerCase()] = k
    return acc
  },
  {} as Record<string, string>,
)

export function getInheritance(category: string): string[] {
  if (!category) return []
  const lower = category.toLowerCase()
  const exactKey = MAP_BY_LOWER[lower]
  if (exactKey) return [...MAP[exactKey]]
  // Pretty-cased label for the unknown class so the chain still
  // shows something readable when the lookup misses.
  const pretty = prettyClassName(category)
  if (/element$/.test(lower)) return [pretty, ...ROOT_CHAIN_ELEMENT]
  if (/type$/.test(lower)) {
    return [
      pretty,
      'IfcTypeProduct',
      'IfcTypeObject',
      'IfcObjectDefinition',
      'IfcRoot',
    ]
  }
  return [pretty, 'IfcRoot']
}

function prettyClassName(category: string): string {
  if (!category) return ''
  if (!/^IFC/i.test(category)) return category
  // Already mixed-case (e.g. "IfcWall"), assume canonical.
  if (category !== category.toUpperCase()) return category
  // ALLCAPS like "IFCBUILDINGSTOREY" → fall back to "Ifc" + the rest
  // lowercased; we don't know word boundaries, so this is best-effort.
  return 'Ifc' + category.slice(3).toLowerCase()
}
